import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { db } from '../supabase'
import { Plus, ArrowRight, Clock, Heart } from 'lucide-react'

const HomePage = () => {
  const { user, profile } = useAuth()
  const [reminders, setReminders] = useState([])
  const [newReminder, setNewReminder] = useState('')
  const [selectedMood, setSelectedMood] = useState('random')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)

  const moods = [
    { value: 'random', label: 'Random', emoji: '🎲' },
    { value: 'poetic', label: 'Poetic', emoji: '📝' },
    { value: 'funny', label: 'Funny', emoji: '😂' },
    { value: 'serious', label: 'Serious', emoji: '🎯' },
    { value: 'weird', label: 'Weird', emoji: '🤪' }
  ]

  const loadUserReminders = useCallback(async () => {
    try {
      const userReminders = await db.getUserReminders(user.id)
      setReminders(userReminders)
    } catch (error) {
      console.error('Error loading reminders:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (user) {
      loadUserReminders()
    }
  }, [user, loadUserReminders])

  const handleSubmitReminder = async (e) => {
    e.preventDefault()
    if (!newReminder.trim() || newReminder.split(' ').length > 3) {
      alert('Please enter a reminder with 3 words or less.')
      return
    }

    setIsSubmitting(true)
    try {
      await db.createReminder(user.id, newReminder.trim(), selectedMood)
      setNewReminder('')
      setSelectedMood('random')
      await loadUserReminders()
    } catch (error) {
      console.error('Error creating reminder:', error)
      alert('Failed to create reminder. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 1) return 'Today'
    if (diffDays === 2) return 'Yesterday'
    if (diffDays <= 7) return `${diffDays - 1} days ago`
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Hi, {profile?.name || 'there'}! 👋
        </h1>
        <p className="text-gray-600 text-lg">
          Ready to create some reminders or rewrite someone else's?
        </p>
      </div>

      {/* Add New Reminder Card */}
      <div className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <Plus className="mr-2" size={20} />
          Add a new reminder
        </h2>
        
        <form onSubmit={handleSubmitReminder} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your reminder (max 3 words)
            </label>
            <input
              type="text"
              value={newReminder}
              onChange={(e) => setNewReminder(e.target.value)}
              placeholder="e.g., buy apples"
              className="input-field"
              maxLength={30}
            />
            <p className="text-sm text-gray-500 mt-1">
              {newReminder.split(' ').length}/3 words
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mood preference
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {moods.map((mood) => (
                <button
                  key={mood.value}
                  type="button"
                  onClick={() => setSelectedMood(mood.value)}
                  className={`p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                    selectedMood === mood.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-lg mb-1">{mood.emoji}</div>
                  <div className="text-xs font-medium">{mood.label}</div>
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !newReminder.trim()}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create Reminder'}
          </button>
        </form>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Link
          to="/rewrite"
          className="card hover:shadow-xl transition-all duration-300 group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Rewrite a stranger's reminder
              </h3>
              <p className="text-gray-600">
                Help someone else by adding meaning to their reminder
              </p>
            </div>
            <ArrowRight className="text-primary-500 group-hover:translate-x-1 transition-transform" size={24} />
          </div>
        </Link>

        <Link
          to="/community"
          className="card hover:shadow-xl transition-all duration-300 group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Explore community
              </h3>
              <p className="text-gray-600">
                See the best rewrites and meet other users
              </p>
            </div>
            <ArrowRight className="text-primary-500 group-hover:translate-x-1 transition-transform" size={24} />
          </div>
        </Link>
      </div>

      {/* Recent Reminders */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <Clock className="mr-2" size={20} />
          Your recent reminders
        </h2>

        {reminders.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">💭</div>
            <p className="text-gray-600 mb-4">No reminders yet!</p>
            <p className="text-sm text-gray-500">
              Create your first reminder above to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {reminders.slice(0, 5).map((reminder) => (
              <div key={reminder.id} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {formatDate(reminder.created_at)}
                    </span>
                    {reminder.mood !== 'random' && (
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                        {moods.find(m => m.value === reminder.mood)?.emoji} {reminder.mood}
                      </span>
                    )}
                  </div>
                  {reminder.rewritten && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      ✨ Rewritten
                    </span>
                  )}
                </div>
                
                <p className="text-lg font-medium text-gray-800 mb-2">
                  "{reminder.text}"
                </p>

                {reminder.rewrites && reminder.rewrites.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 mt-3">
                    <p className="text-sm text-gray-600 mb-2">
                      Rewritten by {reminder.rewrites[0].rewriter.name}:
                    </p>
                    <p className="text-gray-800 italic">
                      "{reminder.rewrites[0].text}"
                    </p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Heart size={14} className="mr-1" />
                        {reminder.rewrites[0].votes || 0} votes
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default HomePage 